package br.com.htms.inspeccaocampo.data.nrvisual

/**
 * Catalogo fechado de itens visiveis em foto/video para o Assistente visual de NRs.
 *
 * Nao cobre obrigacoes documentais (ASO, APR, PGR, CA, treino). Citacoes sao
 * referencia aproximada a consolidacao vigente — o app grava NORMA_VERSAO em cada analise.
 */
object NrVisualCatalog {
    const val NORMA_VERSAO = "MTE consolidado 2024-2025 (itens visuais MVP)"
    const val NORMA_VERSAO_DATA = "2025-01-01"

    const val DISCLAIMER_CURTO =
        "Assistente de apoio a inspecao. Nao e laudo, ART nem inspecao oficial. " +
            "Possivel nao conformidade — validar no local. So achados confirmados pelo TST entram no relatorio."

    const val DISCLAIMER_LONGO =
        "Este modulo e um assistente visual de apoio ao TST/tecnico de seguranca. " +
            "Nao substitui SESMT, laudo, ART ou inspecao oficial. Grande parte das NRs nao aparece em foto " +
            "(procedimento, treinamento, documentacao, medicoes). A IA pode errar (falso positivo/negativo). " +
            "Linguagem padrao: \"possivel nao conformidade — validar no local\". " +
            "Apenas achados com status CONFIRMADO entram no relatorio oficial. " +
            "Itens inspecionados visualmente e limitacoes da camera constam no relatorio."

    const val LIMITACOES_CAMERA =
        "A camera nao cobre: validade de CA, treinamentos, ASO, APR, PGR/GRO documental, " +
            "medicoes (ruido, iluminancia), espacos confinados alem de sinalizacao aparente, " +
            "ergonomia fina (NR-17) e conformidade eletrica completa (NR-10)."

    val cenas: List<NrCenaTipo> = NrCenaTipo.entries

    fun itens(): List<NrCatalogItem> = ITENS

    fun itensParaCena(cena: NrCenaTipo): List<NrCatalogItem> =
        if (cena == NrCenaTipo.GERAL) ITENS
        else ITENS.filter { cena in it.cenas }

    fun porId(id: String): NrCatalogItem? = byId[id]

    private val byId: Map<String, NrCatalogItem> by lazy { ITENS.associateBy { it.id } }

    private fun i(
        id: String,
        cenas: List<NrCenaTipo>,
        nr: String,
        secao: String,
        titulo: String,
        oQueProcurar: String,
        citar: String,
        acao: String,
        gravidade: NrGravidade
    ) = NrCatalogItem(
        id = id,
        cenas = cenas,
        nr = nr,
        secao = secao,
        titulo = titulo,
        oQueProcurar = oQueProcurar,
        citar = citar,
        acao = acao,
        gravidadePadrao = gravidade
    )

    private val O = NrCenaTipo.OBRA
    private val F = NrCenaTipo.OFICINA
    private val E = NrCenaTipo.ELETRICA
    private val A = NrCenaTipo.ALTURA
    private val C = NrCenaTipo.CIRCULACAO
    private val I = NrCenaTipo.INCENDIO
    private val G = NrCenaTipo.GERAL

    private val ITENS: List<NrCatalogItem> = listOf(
        i("NR06_capacete_ausente", listOf(O, A, G), "NR-06", "EPI - capacete",
            "Capacete de seguranca aparentemente ausente",
            "Trabalhador em area de risco sem capacete visivel",
            "NR-06 EPI", "Fornecer/exigir capacete adequado a atividade", NrGravidade.MEDIA),
        i("NR06_oculos_ausente", listOf(O, F, G), "NR-06", "EPI - oculos",
            "Oculos de protecao aparentemente ausentes",
            "Atividade com risco ocular sem oculos/face shield visivel",
            "NR-06 EPI", "Exigir protecao ocular adequada", NrGravidade.MEDIA),
        i("NR06_luva_ausente", listOf(O, F, E), "NR-06", "EPI - luvas",
            "Luvas de protecao aparentemente ausentes",
            "Manipulacao/risco de contato sem luvas visiveis (quando exigidas)",
            "NR-06 EPI", "Exigir luvas adequadas ao risco", NrGravidade.MEDIA),
        i("NR06_bota_ausente", listOf(O, F, G), "NR-06", "EPI - calcado",
            "Calcado de seguranca aparentemente inadequado/ausente",
            "Trabalhador sem bota de seguranca visivel em obra/oficina",
            "NR-06 EPI", "Exigir calcado de seguranca", NrGravidade.MEDIA),
        i("NR06_colete_ausente", listOf(O, C, G), "NR-06", "EPI - colete",
            "Colete refletivo aparentemente ausente",
            "Circulacao em via/obra sem colete de alta visibilidade quando exigido",
            "NR-06 / sinalizacao", "Exigir colete refletivo", NrGravidade.LEVE),
        i("NR06_auricular_ausente", listOf(O, F), "NR-06", "EPI - auricular",
            "Protetor auricular aparentemente ausente",
            "Area ruidosa aparente sem protetor auricular visivel",
            "NR-06 EPI (limitacao: nao mede dB)", "Exigir protecao auditiva se area for de ruido", NrGravidade.LEVE),
        i("NR06_epi_danificado", listOf(O, A, F, G), "NR-06", "EPI - condicao",
            "EPI aparentemente danificado ou inadequado",
            "Capacete rachado, oculos quebrado, luva rasgada ou similar visivel",
            "NR-06 conservacao", "Substituir EPI danificado", NrGravidade.MEDIA),
        i("NR18_abertura_piso", listOf(O, A), "NR-18", "Aberturas",
            "Abertura no piso sem protecao",
            "Buraco, laje, vao de escada ou abertura sem guarda-corpo, tampa ou barreira",
            "NR-18 protecoes contra queda", "Isolar, proteger e sinalizar a abertura", NrGravidade.GRAVE),
        i("NR18_guarda_corpo_ausente", listOf(O, A), "NR-18", "Guarda-corpo",
            "Guarda-corpo aparentemente ausente ou incompleto",
            "Borda de laje, andaime ou plataforma sem guarda-corpo visivel",
            "NR-18 / NR-35", "Instalar guarda-corpo conforme norma", NrGravidade.GRAVE),
        i("NR18_andaime_irregular", listOf(O, A), "NR-18", "Andaime",
            "Andaime aparentemente irregular ou incompleto",
            "Andaime sem plataforma continua, sem rodape, sem travamento aparente ou improvisado",
            "NR-18 andaimes", "Adequar montagem; interditar se risco iminente", NrGravidade.GRAVE),
        i("NR18_circulacao_obstruida", listOf(O, C, I, E), "NR-18", "Circulacao",
            "Via de circulacao obstruida",
            "Passagem, escada ou corredor bloqueado por material/entulho",
            "NR-18 organizacao", "Liberar passagem e organizar materiais", NrGravidade.MEDIA),
        i("NR18_entulho_desorganizado", listOf(O, C), "NR-18", "Organizacao",
            "Entulho ou material desorganizado na area",
            "Acumulo de entulho, ferragem solta ou material sem delimitacao",
            "NR-18", "Organizar, delimitar e destinar residuos", NrGravidade.LEVE),
        i("NR18_area_risco_sem_isolamento", listOf(O, G), "NR-18", "Isolamento",
            "Area de risco sem isolamento/sinalizacao",
            "Zona de queda de material, escavacao ou operacao sem fita/barreira aparente",
            "NR-18", "Isolar e sinalizar a area", NrGravidade.MEDIA),
        i("NR18_escada_inadequada", listOf(O, A, C), "NR-18", "Escadas",
            "Escada de mao aparentemente inadequada",
            "Escada danificada, mal apoiada, sem amarracao ou uso improvisado",
            "NR-18", "Substituir/fixar apoio e amarracao", NrGravidade.MEDIA),
        i("NR18_queda_material", listOf(O, A), "NR-18", "Queda de material",
            "Risco aparente de queda de material",
            "Ferramentas/materiais na borda sem bandeja, rede ou contencao",
            "NR-18", "Instalar protecao contra queda de materiais", NrGravidade.GRAVE),
        i("NR18_iluminacao_deficiente", listOf(O, F, C), "NR-18", "Iluminacao",
            "Iluminacao aparentemente deficiente",
            "Area de trabalho escura ou com sombra critica (limitacao: sem luximetro)",
            "NR-18", "Melhorar iluminacao provisoria/definitiva", NrGravidade.LEVE),
        i("NR35_cinto_ausente", listOf(A, O), "NR-35", "Cinturao",
            "Cinturao de seguranca aparentemente ausente",
            "Trabalho em altura sem cinturao tipo paraquedista visivel",
            "NR-35", "Interromper atividade; exigir cinturao e ancoragem", NrGravidade.IMINENTE),
        i("NR35_talabarte_desconectado", listOf(A, O), "NR-35", "Talabarte",
            "Talabarte aparentemente desconectado",
            "Trabalhador com cinturao mas sem conexao visivel a ponto/linha de vida",
            "NR-35", "Conectar a ancoragem adequada antes de continuar", NrGravidade.IMINENTE),
        i("NR35_linha_vida_ausente", listOf(A, O), "NR-35", "Linha de vida",
            "Linha de vida / ancoragem aparentemente ausente",
            "Area de altura sem linha de vida ou ponto de ancoragem aparente",
            "NR-35", "Instalar sistema de ancoragem certificado", NrGravidade.GRAVE),
        i("NR35_borda_desprotegida", listOf(A, O), "NR-35", "Borda",
            "Borda desprotegida em trabalho em altura",
            "Trabalho proximo a borda sem protecao coletiva ou individual aparente",
            "NR-35 / NR-18", "Proteger borda ou garantir EPIs conectados", NrGravidade.GRAVE),
        i("NR35_plataforma_insegura", listOf(A, O), "NR-35", "Plataforma",
            "Plataforma de trabalho aparentemente insegura",
            "Superficie estreita, instavel ou sem acesso seguro visivel",
            "NR-35", "Adequar plataforma e acesso", NrGravidade.GRAVE),
        i("NR10_painel_aberto", listOf(E, F, O), "NR-10", "Painel",
            "Painel eletrico aberto / partes energizadas aparentes",
            "Quadro/painel aberto com partes vivas visiveis sem barreira",
            "NR-10 (visivel)", "Fechar painel; isolar; so eletricista autorizado", NrGravidade.GRAVE),
        i("NR10_sinalizacao_ausente", listOf(E, O, C), "NR-10", "Sinalizacao",
            "Sinalizacao de risco eletrico aparentemente ausente",
            "Area/painel sem placa de risco eletrico visivel",
            "NR-10 / NR-26", "Instalar sinalizacao adequada", NrGravidade.MEDIA),
        i("NR10_epi_eletrico_ausente", listOf(E), "NR-10", "EPI eletrico",
            "EPI de eletricista aparentemente ausente",
            "Intervencao em painel sem luvas isolantes/capacete/oculos aparentes",
            "NR-10 / NR-06", "Exigir EPI eletrico adequado", NrGravidade.GRAVE),
        i("NR10_bloqueio_ausente", listOf(E, F), "NR-10", "LOTO",
            "Bloqueio/etiquetagem aparentemente ausente",
            "Manutencao em equipamento sem cadeado/etiqueta LOTO visivel na foto",
            "NR-10 (limitacao: LOTO pode estar fora do enquadramento)", "Aplicar LOTO antes da intervencao", NrGravidade.GRAVE),
        i("NR10_cabos_expostos", listOf(E, O, C), "NR-10", "Cabos",
            "Cabos eletricos aparentando exposicao/dano",
            "Fiacao desencapada, emendas improvisadas ou cabos no piso sem protecao",
            "NR-10", "Isolar, proteger ou substituir cabos", NrGravidade.MEDIA),
        i("NR10_extensao_improvisada", listOf(E, O, F), "NR-10", "Extensao",
            "Ligacao eletrica aparentemente improvisada",
            "Benjamin, extensao sobrecarregada ou ligacao precaria visivel",
            "NR-10", "Adequar instalacao por profissional habilitado", NrGravidade.MEDIA),
        i("NR12_protecao_ausente", listOf(F), "NR-12", "Protecao",
            "Protecao de parte movel aparentemente ausente",
            "Polia, correia, engrenagem ou serra sem guarda/protecao visivel",
            "NR-12", "Instalar/reposicionar protecao; interditar se necessario", NrGravidade.IMINENTE),
        i("NR12_emergencia_ausente", listOf(F), "NR-12", "Emergencia",
            "Comando de emergencia aparentemente ausente/inacessivel",
            "Maquina sem botao de emergencia visivel ou obstruido",
            "NR-12", "Garantir emergencia acessivel e funcional", NrGravidade.GRAVE),
        i("NR12_area_operacao_risco", listOf(F, O), "NR-12", "Area de operacao",
            "Area de operacao sem delimitacao aparente",
            "Pessoas proximas a maquina em movimento sem barreira/sinalizacao",
            "NR-12", "Delimitar area e restringir acesso", NrGravidade.MEDIA),
        i("NR12_dispositivo_bypass", listOf(F), "NR-12", "Dispositivos",
            "Dispositivo de seguranca aparentemente burlado",
            "Protecao amarrada aberta, sensor bypass ou similar visivel",
            "NR-12", "Restaurar dispositivo; investigar causa", NrGravidade.IMINENTE),
        i("NR26_placa_ausente", listOf(O, C, E, I, G), "NR-26", "Placas",
            "Placa de sinalizacao aparentemente ausente",
            "Area de risco sem placa de advertencia/obrigacao/proibicao visivel",
            "NR-26", "Instalar sinalizacao conforme risco", NrGravidade.LEVE),
        i("NR26_placa_ilegivel", listOf(O, C, G), "NR-26", "Placas",
            "Placa ilegivel, danificada ou coberta",
            "Sinalizacao pintada, rasgada, coberta por material ou ilegivel",
            "NR-26", "Substituir/limpar sinalizacao", NrGravidade.LEVE),
        i("NR26_cor_piso_apagada", listOf(C, F, O), "NR-26", "Cores",
            "Demarcacao de piso aparentemente apagada",
            "Faixas de circulacao/area de risco pouco visiveis ou apagadas",
            "NR-26", "Repintar demarcacoes", NrGravidade.LEVE),
        i("NR23_extintor_ausente", listOf(I, O, F, C, G), "NR-23", "Extintor",
            "Extintor aparentemente ausente na area",
            "Ambiente sem extintor visivel onde seria esperado (limitacao: pode estar fora do quadro)",
            "NR-23", "Disponibilizar extintor adequado e sinalizado", NrGravidade.MEDIA),
        i("NR23_extintor_obstruido", listOf(I, C, F, O), "NR-23", "Extintor",
            "Extintor obstruido ou inacessivel",
            "Extintor bloqueado por material, sem acesso livre",
            "NR-23", "Liberar acesso ao extintor", NrGravidade.MEDIA),
        i("NR23_rota_obstruida", listOf(I, C, O), "NR-23", "Rota de fuga",
            "Rota de fuga / saida aparentemente obstruida",
            "Corredor ou saida de emergencia bloqueada",
            "NR-23", "Desobstruir rota imediatamente", NrGravidade.GRAVE),
        i("NR23_sinal_saida_ausente", listOf(I, C), "NR-23", "Sinalizacao",
            "Sinalizacao de saida aparentemente ausente",
            "Ambiente sem placa de saida de emergencia visivel",
            "NR-23 / NR-26", "Instalar sinalizacao de saida", NrGravidade.LEVE),
        i("GERAL_ordem_limpeza", listOf(G, O, F, C), "NR-18", "Ordem e limpeza",
            "Ma ordem e limpeza aparente",
            "Desorganizacao geral que aumenta risco de tropeco/incendio",
            "Boas praticas / NR-18", "Organizar area e remover riscos", NrGravidade.LEVE),
        i("GERAL_trabalhador_postura_risco", listOf(O, A, F), "NR-35", "Postura",
            "Postura/posicao aparentemente de risco",
            "Pessoa em local instavel, sobre material solto ou fora de plataforma",
            "NR-35 / NR-18", "Reposicionar em local seguro", NrGravidade.MEDIA),
        i("GERAL_ferramenta_piso", listOf(O, F, C), "NR-18", "Ferramentas",
            "Ferramentas/materiais no piso de circulacao",
            "Objetos no caminho que podem causar tropeco",
            "NR-18", "Recolher e acondicionar", NrGravidade.LEVE),
        i("GERAL_protetor_facial_solda", listOf(F, O), "NR-06", "Solda",
            "Protecao facial/ocular de solda aparentemente ausente",
            "Operacao de solda/corte sem mascara/oculos adequados visiveis",
            "NR-06", "Exigir protecao especifica de solda", NrGravidade.GRAVE),
        i("GERAL_produto_quimico_sem_rotulo", listOf(F, C, O), "NR-26", "Quimicos",
            "Recipiente quimico sem rotulo aparente",
            "Galao/bombona sem identificacao visivel",
            "NR-26 / GHS", "Rotular conforme SDS", NrGravidade.MEDIA),
        i("GERAL_empilhamento_inseguro", listOf(C, O, F), "NR-18", "Empilhamento",
            "Empilhamento aparentemente instavel",
            "Paletes/materiais inclinados ou altos demais sem contencao",
            "NR-18", "Reempilhar com estabilidade", NrGravidade.MEDIA),
        i("GERAL_veiculo_sem_sinalizacao", listOf(O, C), "NR-18", "Veiculos",
            "Equipamento/veiculo sem sinalizacao aparente na area",
            "Maquina/caminhao em operacao sem cone/sinalizador visivel no entorno",
            "NR-18", "Sinalizar operacao de veiculos", NrGravidade.LEVE),
        i("GERAL_pocos_escavacao", listOf(O), "NR-18", "Escavacao",
            "Escavacao sem protecao/sinalizacao aparente",
            "Vala ou escavacao sem barreira, tapume ou sinalizacao",
            "NR-18", "Proteger e sinalizar escavacao", NrGravidade.GRAVE),
        i("GERAL_rede_protecao_ausente", listOf(O, A), "NR-18", "Rede",
            "Rede/bandeja de protecao aparentemente ausente",
            "Fachada/obra em altura sem rede ou bandeja onde esperado",
            "NR-18", "Instalar protecao coletiva contra queda de materiais", NrGravidade.MEDIA),
        i("GERAL_hidrante_obstruido", listOf(I, C), "NR-23", "Hidrante",
            "Hidrante ou mangueira aparentemente obstruido",
            "Acesso a hidrante bloqueado por veiculos/materiais",
            "NR-23", "Liberar acesso ao hidrante", NrGravidade.MEDIA),
        i("NR12_ruido_sem_sinal", listOf(F, O), "NR-26", "Ruido",
            "Area ruidosa sem sinalizacao de EPI auditivo",
            "Maquinas ruidosas aparentes sem placa de protetor auricular",
            "NR-26 / NR-06", "Sinalizar obrigacao de protetor auricular", NrGravidade.LEVE),
        i("NR10_aterramento_aparente_ausente", listOf(E, O), "NR-10", "Aterramento",
            "Equipamento sem indicio visual de aterramento",
            "Carcaca/quadro sem cabo de terra aparente (limitacao forte de inferencia)",
            "NR-10", "Verificar aterramento com profissional habilitado", NrGravidade.MEDIA),
        i("NR18_banheiro_acesso", listOf(O, C), "NR-18", "Instalacoes",
            "Acesso a instalacoes sanitarias aparentemente precario",
            "Caminho para banheiro/vestiario obstruido ou sem sinalizacao (quando visivel)",
            "NR-18", "Garantir acesso seguro as instalacoes", NrGravidade.LEVE),
        i("NR35_escada_marinho_sem_gaiola", listOf(A, O, F), "NR-35", "Escada marinheiro",
            "Escada marinheiro sem protecao aparente",
            "Escada vertical sem gaiola/trilho ou sem uso de trava-quedas visivel",
            "NR-35 / NR-18", "Adequar protecao ou usar trava-quedas", NrGravidade.GRAVE),
        i("NR06_roupa_inadequada", listOf(O, F, E), "NR-06", "Vestimenta",
            "Vestimenta aparentemente inadequada ao risco",
            "Roupa solta perto de maquina, sem manga/calca adequada, etc.",
            "NR-06", "Adequar vestimenta ao risco", NrGravidade.LEVE),
        i("NR18_tapume_ausente", listOf(O, C), "NR-18", "Tapume",
            "Tapume/fechamento de obra aparentemente ausente",
            "Frente de obra sem delimitacao/tapume onde publico circula",
            "NR-18", "Instalar tapume/isolamento", NrGravidade.MEDIA)
    )
}

enum class NrCenaTipo(val id: String, val rotulo: String) {
    OBRA("obra", "Obra civil"),
    OFICINA("oficina", "Oficina / maquina"),
    ELETRICA("eletrica", "Painel eletrico"),
    ALTURA("altura", "Trabalho em altura"),
    CIRCULACAO("circulacao", "Almoxarifado / circulacao"),
    INCENDIO("incendio", "Combate a incendio (visivel)"),
    GERAL("geral", "Cena geral / mista");

    companion object {
        fun fromId(raw: String?): NrCenaTipo =
            entries.find { it.id.equals(raw, ignoreCase = true) || it.name.equals(raw, ignoreCase = true) }
                ?: GERAL
    }
}

enum class NrGravidade(val rotulo: String) {
    LEVE("Leve"),
    MEDIA("Media"),
    GRAVE("Grave"),
    IMINENTE("Iminente");

    companion object {
        fun fromName(raw: String?): NrGravidade? =
            raw?.takeIf { it.isNotBlank() }?.let { runCatching { valueOf(it) }.getOrNull() }
    }
}

data class NrCatalogItem(
    val id: String,
    val cenas: List<NrCenaTipo>,
    val nr: String,
    val secao: String,
    val titulo: String,
    val oQueProcurar: String,
    val citar: String,
    val acao: String,
    val gravidadePadrao: NrGravidade
)
